import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  useBillingOverview,
  useCancelSubscription,
  useCheckout,
  useChangePlan,
  useInvoices,
  usePlans,
  useRevokeCard,
} from 'api/billing';
import apiClient from 'api/client';
import { extractApiError } from 'api/errors';
import { formatDate, formatCurrency } from 'i18n/format';

const OFERTA_VERSION = 'v1';

// 376-ФЗ: cancellation must be one click, so never ConfirmActionDialog's
// type-to-confirm pattern here.
function SimpleConfirmDialog({ open, title, body, confirmLabel, loading, onConfirm, onClose }) {
  const { t: translate } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{body}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('shell.close')}</Button>
        <Button color="error" variant="contained" disabled={loading} onClick={onConfirm}>
          {loading ? translate('shell.oneMoment') : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const STATUS_COLORS = {
  trialing: 'info',
  active: 'success',
  past_due: 'warning',
  grace: 'warning',
  suspended: 'error',
  canceled: 'default',
};

function rubles(kopecks) {
  if (kopecks === null || kopecks === undefined) return '—';
  return formatCurrency(Math.floor(kopecks / 100), 'RUB', { maximumFractionDigits: 0 });
}

export default function BillingTab() {
  const { t: translate } = useTranslation();
  const [awaitingPayment, setAwaitingPayment] = useState(
    () => new URLSearchParams(window.location.search).get('checkout') === '1',
  );
  const pollStartedAt = React.useRef(null);
  const { data: overview, isLoading } = useBillingOverview({
    refetchInterval: awaitingPayment ? 3000 : false,
  });

  React.useEffect(() => {
    if (!awaitingPayment) return;
    if (pollStartedAt.current == null) pollStartedAt.current = Date.now();
    if (overview?.subscription?.status === 'active') {
      setAwaitingPayment(false);
      pollStartedAt.current = null;
    } else if (Date.now() - pollStartedAt.current > 60_000) {
      // 60s cap (§3.8): the webhook is the source of truth, so stop polling.
      setAwaitingPayment(false);
      pollStartedAt.current = null;
    }
  }, [awaitingPayment, overview]);

  if (isLoading) return <CircularProgress />;

  const sub = overview?.subscription;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 900 }}>
      {awaitingPayment && sub?.status !== 'active' && (
        <Alert severity="info">{translate('shell.paymentProcessing')}</Alert>
      )}
      {sub?.status === 'suspended' && (
        <Alert severity="error">{translate('shell.subscriptionSuspended')}</Alert>
      )}
      {sub?.status === 'trialing' && sub?.trial_end && (
        <Alert severity="info">
          {translate('shell.trialUntil', { date: formatDate(sub.trial_end) })}
        </Alert>
      )}
      <CurrentPlanCard overview={overview} />
      <PlanGrid overview={overview} onRedirect={() => setAwaitingPayment(true)} />
      <PaymentMethodCard overview={overview} />
      <InvoicesCard payerType={overview?.payer_type} />
    </Box>
  );
}

function CurrentPlanCard({ overview }) {
  const { t: translate } = useTranslation();
  const sub = overview?.subscription;
  const cancelSub = useCancelSubscription();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState(null);

  const handleCancel = async () => {
    setError(null);
    try {
      await cancelSub.mutateAsync();
      setConfirmOpen(false);
    } catch (err) {
      setError(extractApiError(err, translate('shell.cancelSubscriptionFailed')));
    }
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>{translate('shell.currentPlan')}</Typography>
        <Chip
          size="small"
          label={translate(`shell.billingStatus_${sub?.status || 'free'}`, { defaultValue: sub?.status || 'free' })}
          color={STATUS_COLORS[sub?.status] || 'default'}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
        {overview?.effective_plan}
        {sub?.pending_plan_code && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {translate('shell.pendingPlan', { plan: sub.pending_plan_code })}
          </Typography>
        )}
      </Typography>
      {sub?.current_period_end && (
        <Typography variant="body2" color="text.secondary">
          {sub.cancel_at_period_end
            ? translate('shell.subscriptionEnds', { date: formatDate(sub.current_period_end) })
            : translate('shell.nextRenewal', { date: formatDate(sub.current_period_end) })}
        </Typography>
      )}
      {sub && !sub.cancel_at_period_end && sub.status !== 'canceled' && (
        <Button
          size="small"
          color="error"
          sx={{ mt: 2 }}
          onClick={() => setConfirmOpen(true)}
        >{translate('shell.cancelSubscription')}</Button>
      )}
      <SimpleConfirmDialog
        open={confirmOpen}
        title={translate('shell.cancelSubscriptionTitle')}
        body={translate('shell.cancelSubscriptionBody')}
        confirmLabel={translate('shell.cancelSubscription')}
        onConfirm={handleCancel}
        onClose={() => setConfirmOpen(false)}
        loading={cancelSub.isPending}
      />
    </Paper>
  );
}

function PlanGrid({ overview, onRedirect }) {
  const { t: translate } = useTranslation();
  const { data: plans, isLoading } = usePlans();
  const checkout = useCheckout();
  const changePlan = useChangePlan();
  const [consentPlan, setConsentPlan] = useState(null);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState(null);

  if (isLoading) return <CircularProgress />;

  const sub = overview?.subscription;
  const currentCode = sub?.plan_code;
  const hasActiveSub = sub && ['active', 'past_due', 'grace'].includes(sub.status);

  const selectPlan = (plan) => {
    setError(null);
    if (plan.code === currentCode) return;
    if (hasActiveSub) {
      changePlan
        .mutateAsync({ plan_code: plan.code })
        .catch((err) => setError(extractApiError(err, translate('shell.changePlanFailed'))));
    } else {
      setConsent(false);
      setConsentPlan(plan);
    }
  };

  const startCheckout = async () => {
    setError(null);
    try {
      const result = await checkout.mutateAsync({
        plan_code: consentPlan.code,
        consent: true,
        oferta_version: OFERTA_VERSION,
      });
      setConsentPlan(null);
      if (result.mode === 'redirect') {
        onRedirect();
        window.location.assign(result.confirmation_url);
      }
    } catch (err) {
      setError(extractApiError(err, translate('shell.checkoutFailed')));
      setConsentPlan(null);
    }
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>{translate('shell.plans')}</Typography>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={2}>
        {(plans || []).map((plan) => (
          <Grid item xs={12} sm={6} md={3} key={plan.code}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: plan.code === currentCode ? 'primary.main' : 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>{plan.name}</Typography>
              <Typography variant="h6">
                {plan.price_kopecks === null ? translate('shell.onRequest') : translate('shell.priceMonthly', { price: rubles(plan.price_kopecks) })}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                {translate('shell.planEntitlements', { warehouses: plan.entitlements?.max_warehouses ?? '∞', pipelines: plan.entitlements?.max_pipelines ?? '∞', seats: plan.entitlements?.max_seats ?? '∞' })}
                {plan.entitlements?.dbt_enabled ? ' · dbt' : ''}
              </Typography>
              <Button
                fullWidth
                size="small"
                variant={plan.code === currentCode ? 'outlined' : 'contained'}
                disabled={
                  plan.code === currentCode ||
                  plan.price_kopecks === null ||
                  checkout.isPending ||
                  changePlan.isPending
                }
                onClick={() => selectPlan(plan)}
              >
                {plan.code === currentCode ? translate('shell.current') : hasActiveSub ? translate('shell.change') : translate('shell.select')}
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(consentPlan)} onClose={() => setConsentPlan(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{translate('shell.subscribePlan', { name: consentPlan?.name })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {translate('shell.checkoutTerms', { price: rubles(consentPlan?.price_kopecks) })}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
            label={
              <Typography variant="body2">
                {translate('shell.checkoutConsent', { version: OFERTA_VERSION })}
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentPlan(null)}>{translate('shell.cancel')}</Button>
          <Button variant="contained" disabled={!consent || checkout.isPending} onClick={startCheckout}>
            {checkout.isPending ? translate('shell.openCheckout') : translate('shell.goToCheckout')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function PaymentMethodCard({ overview }) {
  const { t: translate } = useTranslation();
  const method = overview?.payment_method;
  const revoke = useRevokeCard();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{translate('shell.paymentMethod')}</Typography>
      {method ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CreditCardIcon color="action" />
          <Typography variant="body2">
            {method.brand || translate('shell.card')} {method.masked_pan}
          </Typography>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setConfirmOpen(true)}
          >{translate('shell.removeCard')}</Button>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">{translate('shell.noCard')}</Typography>
      )}
      <SimpleConfirmDialog
        open={confirmOpen}
        title={translate('shell.removeCardTitle')}
        body={translate('shell.removeCardBody')}
        confirmLabel={translate('shell.removeCard')}
        onConfirm={async () => {
          await revoke.mutateAsync(method.id);
          setConfirmOpen(false);
        }}
        onClose={() => setConfirmOpen(false)}
        loading={revoke.isPending}
      />
    </Paper>
  );
}

async function downloadSchet(invoice) {
  // Authenticated blob download — a bare <a href> would not carry the JWT.
  const resp = await apiClient.get(`/api/v1/billing/invoices/${invoice.id}/pdf/`, {
    responseType: 'blob',
  });
  const isPdf = (resp.headers['content-type'] || '').includes('pdf');
  const url = URL.createObjectURL(resp.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `schet-${invoice.number}.${isPdf ? 'pdf' : 'html'}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function InvoicesCard({ payerType }) {
  const { t: translate } = useTranslation();
  const { data, isLoading } = useInvoices();
  const invoices = Array.isArray(data) ? data : data?.results || [];

  if (isLoading) return <CircularProgress />;
  if (!invoices.length) return null;

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>{translate('shell.invoices')}</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>№</TableCell>
              <TableCell>{translate('shell.period')}</TableCell>
              <TableCell>{translate('shell.amount')}</TableCell>
              <TableCell>{translate('shell.status')}</TableCell>
              {payerType === 'company' && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell>{inv.number}</TableCell>
                <TableCell>
                  {formatDate(inv.period_start)} — {formatDate(inv.period_end)}
                </TableCell>
                <TableCell>{rubles(inv.total_kopecks)}</TableCell>
                <TableCell>
                  <Chip size="small" label={translate(`shell.billingStatus_${inv.status}`, { defaultValue: inv.status })} color={inv.status === 'paid' ? 'success' : 'default'} />
                </TableCell>
                {payerType === 'company' && (
                  <TableCell>
                    <Link component="button" type="button" onClick={() => downloadSchet(inv)}>{translate('shell.downloadInvoice')}</Link>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
